import { supabase } from '@/lib/supabaseClient'
import { organizationService } from './organization.service'
import type {
  Submission,
  SubmissionWithDocuments,
  SubmissionWithDetails,
  CreateSubmissionInput,
  UpdateSubmissionInput,
  SubmissionFilters,
  SubmissionListResponse
} from '@/types'

class SubmissionService {
  /**
   * Create a new submission
   */
  async createSubmission(input: CreateSubmissionInput): Promise<Submission> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Get user's organization, creating one if needed
    let { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    // If user doesn't have an organization, try to set them up
    if (!profile?.organization_id) {
      console.log('User has no organization, attempting to create one...')
      try {
        const setup = await organizationService.ensureUserSetup()
        
        if (!setup.organization) {
          throw new Error('Unable to create organization. Please try again or contact support.')
        }
        
        // Re-fetch the profile after setup
        const { data: refreshedProfile } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single()
        
        profile = refreshedProfile
      } catch (err) {
        console.error('Setup error:', err)
        throw err
      }
    }

    if (!profile?.organization_id) {
      throw new Error('User is not part of an organization')
    }

    const { data, error } = await supabase
      .from('submissions')
      .insert({
        organization_id: profile.organization_id,
        created_by: user.id,
        insured_name: input.insured_name,
        status: 'draft'
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating submission:', error)
      throw error
    }

    return data
  }

  /**
   * Get submission by ID with documents
   */
  async getSubmission(id: string): Promise<SubmissionWithDetails | null> {
    const { data, error } = await supabase
      .from('submissions')
      .select(`
        *,
        documents (
          *,
          extracted_fields:extracted_risk_fields (*)
        ),
        created_by_user:users!submissions_created_by_fkey (*)
      `)
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching submission:', error)
      return null
    }

    return data as unknown as SubmissionWithDetails
  }

  /**
   * List submissions with filters
   */
  async listSubmissions(filters: SubmissionFilters = {}): Promise<SubmissionListResponse> {
    const {
      status = 'all',
      search,
      sortBy = 'created_at',
      sortOrder = 'desc',
      page = 1,
      limit = 10
    } = filters

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) {
      return { submissions: [], total: 0, page, limit, totalPages: 0 }
    }

    let query = supabase
      .from('submissions')
      .select('*, documents(*)', { count: 'exact' })
      .eq('organization_id', profile.organization_id)

    // Apply status filter
    if (status !== 'all') {
      query = query.eq('status', status)
    }

    // Apply search filter
    if (search) {
      query = query.ilike('insured_name', `%${search}%`)
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' })

    // Apply pagination
    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) {
      console.error('Error listing submissions:', error)
      throw error
    }

    const total = count || 0
    const totalPages = Math.ceil(total / limit)

    return {
      submissions: (data || []) as SubmissionWithDocuments[],
      total,
      page,
      limit,
      totalPages
    }
  }

  /**
   * Update submission
   */
  async updateSubmission(
    id: string,
    updates: UpdateSubmissionInput
  ): Promise<Submission> {
    const { data, error } = await supabase
      .from('submissions')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating submission:', error)
      throw error
    }

    return data
  }

  /**
   * Delete submission
   */
  async deleteSubmission(id: string): Promise<void> {
    const { error } = await supabase
      .from('submissions')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting submission:', error)
      throw error
    }
  }

  /**
   * Archive submission
   */
  async archiveSubmission(id: string): Promise<Submission> {
    return this.updateSubmission(id, { status: 'archived' })
  }

  /**
   * Get submission counts by status
   */
  async getSubmissionCounts(): Promise<Record<string, number>> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return {}

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) return {}

    const statuses = ['draft', 'processing', 'review', 'complete', 'archived']
    const counts: Record<string, number> = {}

    for (const status of statuses) {
      const { count } = await supabase
        .from('submissions')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', profile.organization_id)
        .eq('status', status)

      counts[status] = count || 0
    }

    // Total count
    const { count: total } = await supabase
      .from('submissions')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', profile.organization_id)

    counts.all = total || 0

    return counts
  }
}

export const submissionService = new SubmissionService()
